import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SessionProvider } from './lib/session'
import { AppShell } from './components/AppShell'
import { RequireAuth } from './components/RequireAuth'
import { RequirePermissionRoute } from './components/RequirePermissionRoute'
import { SessionGate } from './components/SessionGate'
import { Account } from './routes/Account'
import { AccountsReceivable } from './routes/AccountsReceivable'
import { Alerts } from './routes/Alerts'
import { AlertSettings } from './routes/AlertSettings'
import { AuditLog } from './routes/AuditLog'
import { BillingExceptions } from './routes/BillingExceptions'
import { BillingForecast } from './routes/BillingForecast'
import { Budget } from './routes/Budget'
import { CashFlow } from './routes/CashFlow'
import { Eula } from './routes/Eula'
import { ExecutiveDashboard } from './routes/ExecutiveDashboard'
import { WeeklyDashboard } from './routes/WeeklyDashboard'
import { Integrations } from './routes/Integrations'
import { Login } from './routes/Login'
import { NotFound } from './routes/NotFound'
import { Overview } from './routes/Overview'
import { Productivity } from './routes/Productivity'
import { Privacy } from './routes/Privacy'
import { Refunds } from './routes/Refunds'
import { Roles } from './routes/Roles'
import { TrustFunding } from './routes/TrustFunding'
import { Users } from './routes/Users'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Financial figures are synced on a schedule, not per-keystroke.
       * Refetching on focus would imply a freshness the data does not have.
       */
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      /**
       * A 401 or 403 is an answer, not a transient failure. Retrying one adds
       * two more denied requests to the audit trail and delays the screen that
       * explains the refusal.
       */
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status
        if (status !== undefined && status < 500) return false
        return failureCount < 2
      },
    },
  },
})

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <SessionGate>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />

              {/**
               * The licence agreement and the privacy notice are the only pages
               * served without a session, and they have to be: Intuit will not
               * publish a QuickBooks Online app whose EULA and privacy policy
               * are not reachable at a plain URL by a reviewer with no account
               * here, and someone deciding whether to sign in at all should be
               * able to read both first.
               *
               * They sit outside `RequireAuth` rather than inside it with an
               * exemption, so there is no path by which a guard change can
               * quietly put them behind sign-in.
               */}
              <Route path="/legal/eula" element={<Eula />} />
              <Route path="/legal/privacy" element={<Privacy />} />

              {/**
               * Everything else sits behind the session, inside the shell. Page
               * guards are per-route so an unreachable page explains itself
               * instead of rendering empty.
               */}
              <Route
                element={
                  <RequireAuth>
                    <AppShell>
                      <Outlet />
                    </AppShell>
                  </RequireAuth>
                }
              >
                <Route path="/" element={<Overview />} />
                <Route path="/account" element={<Account />} />
                <Route path="/roles" element={<Roles />} />
                <Route
                  path="/users"
                  element={
                    <RequirePermissionRoute permission="users.manage">
                      <Users />
                    </RequirePermissionRoute>
                  }
                />
                {/**
                 * Billing exceptions are reachable by either view grant — Finance
                 * firm-wide, Legal Production scoped to its own matters — so the
                 * guard grants on any of them. A single permission here told
                 * Finance it had no access to a queue the API served happily.
                 */}
                <Route
                  path="/billing-exceptions"
                  element={
                    <RequirePermissionRoute
                      permission={[
                        'billing_exceptions.view_all',
                        'billing_exceptions.view_own_matters',
                      ]}
                    >
                      <BillingExceptions />
                    </RequirePermissionRoute>
                  }
                />
                <Route
                  path="/billing-forecast"
                  element={
                    <RequirePermissionRoute permission="firm_financials.view">
                      <BillingForecast />
                    </RequirePermissionRoute>
                  }
                />
                {/**
                 * Cash flow sits behind `cash_flow.view` — its own grant, held
                 * by Finance and Executive. Maintaining the obligations under
                 * it needs `cash_flow.manage`, which is checked inside the page
                 * rather than at the route: Executive is meant to *read* this
                 * forecast, and guarding the whole page on the write grant
                 * would send it to the Forbidden screen instead. Same shape as
                 * Refunds, for the same reason.
                 */}
                <Route
                  path="/cash-flow"
                  element={
                    <RequirePermissionRoute permission="cash_flow.view">
                      <CashFlow />
                    </RequirePermissionRoute>
                  }
                />
                {/**
                 * Legal Production KPIs and the weekly report queue, behind
                 * `reports.view` — the grant the matrix already describes as
                 * reading reporting packs. Editing a draft and sending one are
                 * checked in-page against their own narrower grants, so an
                 * Executive reads the pack and the send button is simply not
                 * there.
                 */}
                <Route
                  path="/productivity"
                  element={
                    <RequirePermissionRoute permission="reports.view">
                      <Productivity />
                    </RequirePermissionRoute>
                  }
                />
                {/**
                 * Budget vs. actual and the scenarios beside it, behind
                 * `budgets.view`. Importing a budget and building a scenario
                 * need `budgets.manage`, checked inside the page for the same
                 * reason Refunds does: Executive is meant to read this, and
                 * guarding the whole page on the write grant would send it to
                 * the Forbidden screen.
                 */}
                <Route
                  path="/budget"
                  element={
                    <RequirePermissionRoute permission="budgets.view">
                      <Budget />
                    </RequirePermissionRoute>
                  }
                />
                {/**
                 * Accounts receivable has its own grant rather than sharing
                 * `firm_financials.view` with the forecast, because the matrix
                 * draws that line — Executive reaches both, and a future role
                 * could hold one without the other.
                 */}
                {/**
                 * The consolidated executive dashboard (KAN-60). Behind
                 * `firm_financials.view`, the same grant as the forecast — the
                 * matrix already describes it as the consolidated dashboards.
                 *
                 * That guards the page. Each tile additionally declares the
                 * grant its own figure needs and is withheld without it, which
                 * is checked server-side where the snapshot is read: a
                 * consolidated view is the obvious place for a permission
                 * bypass to appear, so it is not left to this route.
                 */}
                <Route
                  path="/executive"
                  element={
                    <RequirePermissionRoute permission="firm_financials.view">
                      <ExecutiveDashboard />
                    </RequirePermissionRoute>
                  }
                />
                {/**
                 * The weekly financial dashboard. Same page grant as the
                 * executive dashboard; each section is then withheld
                 * server-side unless the viewer holds its own screen's grant.
                 */}
                <Route
                  path="/weekly"
                  element={
                    <RequirePermissionRoute permission="firm_financials.view">
                      <WeeklyDashboard />
                    </RequirePermissionRoute>
                  }
                />
                <Route
                  path="/accounts-receivable"
                  element={
                    <RequirePermissionRoute permission="ar.view">
                      <AccountsReceivable />
                    </RequirePermissionRoute>
                  }
                />
                {/**
                 * Refunds are gated on the view grant alone. Deciding on one
                 * needs `refunds.manage`, and that is checked inside the page
                 * rather than at the route: Executive is meant to *read* this
                 * dashboard, and guarding the whole page on the write grant
                 * would send it to the Forbidden screen instead.
                 */}
                <Route
                  path="/refunds"
                  element={
                    <RequirePermissionRoute permission="refunds.view">
                      <Refunds />
                    </RequirePermissionRoute>
                  }
                />
                {/**
                 * Trust funding sits behind `trust_ledger.view`, the grant the
                 * matrix already describes as IOLTA balances and the movement
                 * history. Finance holds it; Executive's read-only access stops
                 * short of client trust balances, which is a decision in the
                 * matrix rather than one taken here.
                 */}
                <Route
                  path="/trust-funding"
                  element={
                    <RequirePermissionRoute permission="trust_ledger.view">
                      <TrustFunding />
                    </RequirePermissionRoute>
                  }
                />
                {/**
                 * Exception alerts (KAN-43 / KAN-81). Behind `alerts.view`,
                 * which opens the page; *which* alerts it shows is decided
                 * server-side from the grant each rule declares for its own
                 * subject, so an Executive sees the refund alerts and not the
                 * trust ones.
                 *
                 * The thresholds screen carries the narrower grant. Setting
                 * what the firm is told about is not a read-only activity, and
                 * guarding it here rather than in-page is right because the
                 * whole page is the write — unlike Refunds, where the page is a
                 * dashboard with some controls on it.
                 */}
                <Route
                  path="/alerts"
                  element={
                    <RequirePermissionRoute permission="alerts.view">
                      <Alerts />
                    </RequirePermissionRoute>
                  }
                />
                <Route
                  path="/alerts/settings"
                  element={
                    <RequirePermissionRoute permission="alerts.manage">
                      <AlertSettings />
                    </RequirePermissionRoute>
                  }
                />
                <Route
                  path="/audit-log"
                  element={
                    <RequirePermissionRoute permission="audit_log.view">
                      <AuditLog />
                    </RequirePermissionRoute>
                  }
                />
                <Route
                  path="/integrations"
                  element={
                    <RequirePermissionRoute permission="integrations.manage">
                      <Integrations />
                    </RequirePermissionRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </SessionGate>
      </SessionProvider>
    </QueryClientProvider>
  )
}
